import { CaseStatus, convertUserToContact, } from '@debbie/common/dist/cases/index.js';
import { getContactPerson, } from '@debbie/common/dist/customers/index.js';
import { TimespanUnit } from '@debbie/common/dist/index.js';
import { InteractionType, } from '@debbie/common/dist/interactions/index.js';
import { UpdateState, } from '@debbie/common/dist/updates/index.js';
import { PathType, PathWarning, RequirementType, TimingType, TodoType, isPossiblePath, isPossibleTask, } from '@debbie/common/dist/workflows/index.js';
import React from 'react';
import { styled } from 'styled-components';
import THEME from '../../theme.js';
import { EDGE_LABEL_HEIGHT, EDGE_LABEL_WIDTH } from './constants.js';
import UpdateType from './UpdateType.js';
export var NodeTypes;
(function (NodeTypes) {
    NodeTypes["StartingPoint"] = "STARTING_POINT";
})(NodeTypes || (NodeTypes = {}));
const fromTaskToNode = (task, paths, updateTypesMap, onDelete, caseContext, workflowId) => {
    const _updateType = updateTypesMap[task.updateTypeId];
    if (!_updateType) {
        throw `Could not find update type (${task.updateTypeId}) for task`;
    }
    const nodePaths = paths.filter((path) => path.target === task.id);
    const _isPossibleTask = caseContext &&
        (isPossibleTask(task, {
            isCompany: caseContext.singleCase.isCompany,
            caseStatus: caseContext.singleCase.status,
            endReasonId: caseContext.singleCase.endReasonId,
            contact: convertUserToContact(getContactPerson(caseContext.customer, caseContext.singleCase)),
            updates: caseContext.updates,
            caseGroupId: caseContext.singleCase.caseGroup?.id || null,
            caseId: caseContext.singleCase.caseId,
            caseVouchers: caseContext.caseVouchers,
            creditorId: caseContext.singleCase.creditorId,
            hasFoundation: caseContext.hasFoundation,
            foundationTypes: caseContext.foundationTypes,
            customerTags: caseContext.customer.tags,
            caseTags: caseContext.singleCase.tags,
            interactions: caseContext.interactions,
            properties: caseContext.properties,
        }, nodePaths, [PathType.Normal]) ||
            (!task.startingPoint &&
                nodePaths.filter((path) => path.type === PathType.Normal).length === 0));
    const executedUpdate = caseContext &&
        caseContext.updates.find((update) => update.state === UpdateState.Executed && update.taskId === task?.id);
    return {
        id: task.id,
        position: task.position,
        selectable: caseContext ? _isPossibleTask || !!executedUpdate : true,
        data: {
            updateTypeId: _updateType.updateTypeId,
            workflowStageId: task.workflowStageId ?? null,
            pauseAtTiming: task.pauseAtTiming,
            label: (React.createElement(UpdateType, { updateType: _updateType, task: task, workflowStageId: task.workflowStageId ?? null, onDelete: onDelete, caseContext: caseContext, isPossibleTask: _isPossibleTask, workflowId: workflowId })),
            alternativePaths: nodePaths.filter((path) => [PathType.ForMigration, PathType.DeprecatedTask].includes(path.type)),
        },
        className: 'task-node',
    };
};
const getNodesFromWorkflow = (workflow, updateTypesMap, onDelete, caseContext, workflowId) => {
    return workflow.tasks.map((task) => {
        return fromTaskToNode(task, workflow.paths, updateTypesMap, onDelete, caseContext, workflowId);
    });
};
const getTasksFromNodes = (nodes, edges) => {
    const toTasks = nodes
        .map((node) => {
        const edgesToNode = edges.filter((edge) => edge.target === node.id);
        const startingPointEdge = edgesToNode.find((edge) => edge.source === NodeTypes.StartingPoint);
        return {
            id: node.id,
            position: node.position,
            updateTypeId: node.data.updateTypeId,
            workflowStageId: node.data.workflowStageId ?? null,
            pauseAtTiming: node.data.pauseAtTiming,
            startingPoint: !!startingPointEdge,
            startingPointRequirements: startingPointEdge && startingPointEdge.data.requirements,
            startingPointPriority: startingPointEdge && startingPointEdge.data.priority,
            startingPointManual: startingPointEdge && startingPointEdge.data.manual,
        };
    })
        .filter((task) => !Object.values(NodeTypes).includes(task.id));
    // Remove duplicates - there have been a bug where the same task was added multiple times. Please remove later
    return toTasks.filter((path, index) => toTasks.findIndex((p) => p.id === path.id) === index);
};
const getPathsFromEdges = (edges, nodes) => {
    const toPaths = edges
        .map((edge) => {
        return {
            id: edge.id,
            source: edge.source,
            target: edge.target,
            priority: edge.data.priority,
            manual: edge.data.manual,
            type: edge.data.type,
            requirements: edge.data.requirements,
            timing: edge.data.timing,
        };
    })
        .filter((path) => !Object.values(NodeTypes).includes(path.target) &&
        !Object.values(NodeTypes).includes(path.source))
        .concat(nodes.reduce((acc, node) => {
        acc = acc.concat(node.data.alternativePaths || []);
        return acc;
    }, []));
    // Remove duplicates - there have been a bug where the same task was added multiple times. Please remove later
    return toPaths.filter((path, index) => toPaths.findIndex((p) => p.id === path.id) === index);
};
const getEdgeFromPath = (path, tasks, updateTypesMap, caseContext) => {
    const targetTask = path.source == NodeTypes.StartingPoint &&
        tasks.find((task) => task.id === path.target);
    const _isPossiblePath = caseContext &&
        isPossiblePath({
            ...path,
        }, {
            contact: convertUserToContact(getContactPerson(caseContext.customer, caseContext.singleCase)),
            isCompany: caseContext.singleCase.isCompany,
            endReasonId: caseContext.singleCase.endReasonId,
            caseStatus: caseContext.singleCase.status,
            updates: caseContext.updates,
            caseVouchers: caseContext.caseVouchers,
            caseGroupId: caseContext.singleCase.caseGroup?.id || null,
            caseId: caseContext.singleCase.caseId,
            creditorId: caseContext.singleCase.creditorId,
            hasFoundation: caseContext.hasFoundation,
            foundationTypes: caseContext.foundationTypes,
            customerTags: caseContext.customer.tags,
            caseTags: caseContext.singleCase.tags,
            interactions: caseContext.interactions,
            properties: caseContext.properties,
        });
    const requirements = path.requirements || [];
    let warnings = [];
    if (!caseContext) {
        const task = tasks.find((task) => task.id === path.target);
        const updateType = updateTypesMap && task && updateTypesMap[task.updateTypeId];
        if (task && updateType) {
            warnings = getPathWarnings(updateType, requirements);
        }
    }
    return {
        id: path.id,
        source: path.source,
        target: path.target,
        animated: caseContext ? _isPossiblePath : true,
        type: 'custom',
        data: {
            caseContext,
            priority: typeof path.priority === 'number' ? path.priority : 1,
            requirements,
            warnings,
            manual: path.source == NodeTypes.StartingPoint
                ? targetTask && targetTask.startingPoint
                    ? targetTask.startingPointManual
                    : false
                : path.manual,
            timing: path.timing,
            type: path.type,
        },
    };
};
const getEdgesFromWorkflow = (workflow, updateTypesMap, caseContext) => {
    const edges = workflow.paths
        .filter((path) => ![PathType.DeprecatedTask, PathType.ForMigration].includes(path.type))
        .map((path) => getEdgeFromPath(path, workflow.tasks, updateTypesMap, caseContext))
        .concat(workflow.tasks
        .filter((task) => task.startingPoint)
        .map((task) => getEdgeFromPath({
        id: `${task.id}-${NodeTypes.StartingPoint}`,
        target: task.id,
        source: NodeTypes.StartingPoint,
        manual: false,
        priority: typeof task.startingPointPriority === 'number'
            ? task.startingPointPriority
            : 1,
        requirements: task.startingPointRequirements || [],
        type: PathType.Normal,
        timing: {
            type: TimingType.Timespan,
            details: {
                timespan: [0, TimespanUnit.Days],
            },
        },
    }, workflow.tasks, updateTypesMap, caseContext)));
    // Sibling clustering is applied at render time via displayEdges (which uses
    // the live, measured nodes), so we only build the raw edges here.
    return edges;
};
const LABEL_BOX_WIDTH = EDGE_LABEL_WIDTH;
const LABEL_BOX_HEIGHT = EDGE_LABEL_HEIGHT;
const LABEL_OVERLAP_PADDING = 6;
const DEFAULT_TASK_NODE_WIDTH = 282;
const DEFAULT_TASK_NODE_HEIGHT = 120;
const applyEdgeLabelClusters = (edges, nodes) => {
    const nodeBoxes = new Map();
    for (const node of nodes) {
        if (!node.position) {
            continue;
        }
        nodeBoxes.set(node.id, {
            x: node.position.x,
            y: node.position.y,
            width: node.width ?? DEFAULT_TASK_NODE_WIDTH,
            height: node.height ?? DEFAULT_TASK_NODE_HEIGHT,
        });
    }
    const edgeMidpoints = new Map();
    for (const edge of edges) {
        const source = nodeBoxes.get(edge.source);
        const target = nodeBoxes.get(edge.target);
        if (source && target) {
            const sourceBottomCenter = {
                x: source.x + source.width / 2,
                y: source.y + source.height,
            };
            const targetTopCenter = {
                x: target.x + target.width / 2,
                y: target.y,
            };
            edgeMidpoints.set(edge.id, {
                x: (sourceBottomCenter.x + targetTopCenter.x) / 2,
                y: (sourceBottomCenter.y + targetTopCenter.y) / 2,
            });
        }
    }
    const overlaps = (a, b) => {
        const ma = edgeMidpoints.get(a);
        const mb = edgeMidpoints.get(b);
        if (!ma || !mb) {
            return false;
        }
        return (Math.abs(ma.x - mb.x) < LABEL_BOX_WIDTH + LABEL_OVERLAP_PADDING &&
            Math.abs(ma.y - mb.y) < LABEL_BOX_HEIGHT + LABEL_OVERLAP_PADDING);
    };
    const parent = new Map();
    const find = (id) => {
        let root = id;
        while (parent.get(root) !== root) {
            root = parent.get(root) ?? root;
        }
        let current = id;
        while (current !== root) {
            const next = parent.get(current) ?? current;
            parent.set(current, root);
            current = next;
        }
        return root;
    };
    const union = (a, b) => {
        const rootA = find(a);
        const rootB = find(b);
        if (rootA !== rootB) {
            parent.set(rootA, rootB);
        }
    };
    for (const edge of edges) {
        parent.set(edge.id, edge.id);
    }
    for (let i = 0; i < edges.length; i++) {
        for (let j = i + 1; j < edges.length; j++) {
            if (overlaps(edges[i].id, edges[j].id)) {
                union(edges[i].id, edges[j].id);
            }
        }
    }
    const clusterMap = new Map();
    for (const edge of edges) {
        const root = find(edge.id);
        const cluster = clusterMap.get(root) ?? [];
        cluster.push(edge);
        clusterMap.set(root, cluster);
    }
    const siblingInfo = new Map();
    for (const cluster of clusterMap.values()) {
        cluster.sort((a, b) => (a.data?.priority || 0) - (b.data?.priority || 0));
        cluster.forEach((edge, index) => {
            siblingInfo.set(edge.id, {
                siblingIndex: index,
                siblingCount: cluster.length,
            });
        });
    }
    return edges.map((edge) => {
        const info = siblingInfo.get(edge.id);
        if (!info) {
            return edge;
        }
        if (edge.data?.siblingIndex === info.siblingIndex &&
            edge.data?.siblingCount === info.siblingCount) {
            return edge;
        }
        return {
            ...edge,
            data: {
                ...edge.data,
                siblingIndex: info.siblingIndex,
                siblingCount: info.siblingCount,
            },
        };
    });
};
const getStartingPointNode = () => {
    return {
        id: NodeTypes.StartingPoint,
        position: { x: 500, y: 120 },
        draggable: false,
        selectable: false,
        data: {
            label: (React.createElement(StartingPoint, null,
                React.createElement("div", { className: "circle pulse" }))),
        },
        type: 'input',
        className: 'task-node starting-point',
    };
};
const getDefaultRequirements = (updateType) => {
    let requirements = [];
    if (!updateType) {
        return requirements;
    }
    if (updateType.procedure?.find((todo) => todo.type === TodoType.CreateInteraction &&
        todo.details.type === InteractionType.Eboks)) {
        requirements.push({
            id: crypto.randomUUID(),
            type: RequirementType.ReadyForEboks,
            details: {},
        });
    }
    if (updateType.procedure?.find((todo) => todo.type === TodoType.CreateInteraction &&
        todo.details.type === InteractionType.Letter)) {
        requirements.push({
            id: crypto.randomUUID(),
            type: RequirementType.HasValidAddress,
            details: {},
        });
    }
    if (updateType.procedure?.find((todo) => todo.type === TodoType.CreateInteraction &&
        todo.details.type === InteractionType.Email)) {
        requirements.push({
            id: crypto.randomUUID(),
            type: RequirementType.HasValidEmail,
            details: {},
        });
    }
    return requirements;
};
const getPathWarnings = (updateType, requirements) => {
    const warnings = [];
    // Ready for e-Boks requirement
    if (!requirements.find((requirement) => requirement.type === RequirementType.ReadyForEboks) &&
        updateType.procedure?.find((todo) => todo.type === TodoType.CreateInteraction &&
            todo.details.type === InteractionType.Eboks)) {
        warnings.push(PathWarning.ReadyForEboksRequirementRecommended);
    }
    // Valid e-mail requirement
    if (!requirements.find((requirement) => requirement.type === RequirementType.HasValidEmail) &&
        updateType.procedure?.find((todo) => todo.type === TodoType.CreateInteraction &&
            todo.details.type === InteractionType.Email)) {
        warnings.push(PathWarning.ValidEmailRequirementRecommended);
    }
    // Valid address requirement
    if (!requirements.find((requirement) => requirement.type === RequirementType.HasValidAddress) &&
        updateType.procedure?.find((todo) => todo.type === TodoType.CreateInteraction &&
            todo.details.type === InteractionType.Letter)) {
        warnings.push(PathWarning.ValidAddressRequirementRecommended);
    }
    return warnings;
};
export default {
    getEdgesFromWorkflow,
    getNodesFromWorkflow,
    fromTaskToNode,
    getStartingPointNode,
    getEdgeFromPath,
    getTasksFromNodes,
    getPathsFromEdges,
    getDefaultRequirements,
    getPathWarnings,
    applyEdgeLabelClusters,
};
const StartingPoint = styled.div `
  background-color: ${THEME.STATUS_COLORS[CaseStatus.VERIFIED]};
  color: ${THEME.COLOR_SECONDARY};
  padding: 20px;
  margin: -16px;
  border-radius: 30px;
  font-size: 1.2em;
  font-weight: 900;
  text-transform: uppercase;
  overflow: hidden;

  .circle {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    box-shadow: 0px 0px 1px 1px #0000001a;
    background: white;
    margin: 0 3px;
  }

  .pulse {
    animation: pulse-animation 2s infinite;
  }

  @keyframes pulse-animation {
    0% {
      box-shadow: 0 0 0 0px rgba(255, 255, 255, 0.2);
    }
    100% {
      box-shadow: 0 0 0 30px rgba(255, 255, 255, 0);
    }
  }
`;
//# sourceMappingURL=helpers.js.map